import { IconName, Toolbar } from '@infotech-soe/erp-ui-kit';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { IntlFormatters, useIntl } from 'react-intl';

import { BuiltinCaptionType } from '../../../models/caption/Caption';
import { TToolbarConfig } from '../../../models/user-config/DisplayModeConfig';
import CommandViewModel, {
  CommandListEntryType,
  CommandListEntryViewModel,
} from '../../../services/commands/CommandViewModel';
import { getCaptionFullText, getCaptionText } from '../Caption';

export const enum OptionType {
  SEPARATOR = 'separator',
  BUTTON = 'button',
}

export interface ButtonOption {
  type: OptionType.BUTTON;
  icon: IconName;
  name: string;
  disabled?: boolean;
  label: string;
  badge?: string | number;
}

type BadgeItems = Record<
  CommandListEntryViewModel['name'],
  string | number | undefined
>;

interface Props {
  conditionsCount: number | string;
  commands?: CommandViewModel[];
  onInitiateCommand: (name: string) => void;
  toolbarCommandsConfig?: Record<string, TToolbarConfig>;
  className?: string;
}

interface SeparatorOption {
  type: OptionType.SEPARATOR;
}

type ToolbarOption = ButtonOption | SeparatorOption;

const LAST_SHOWN_BUTTON_NAME = 'settings';

const lastElement = (arr: any[]) => arr.at(-1);
const shouldAddSeparator = (arr: ToolbarOption[]) => {
  const element = lastElement(arr);
  return element && element.type !== OptionType.SEPARATOR;
};
const createSeparator = (): SeparatorOption => ({ type: OptionType.SEPARATOR });

export const createOptions = (
  commands: CommandViewModel[],
  formatMessage: IntlFormatters['formatMessage'],
  badges?: BadgeItems
): ToolbarOption[] => {
  const options = commands
    .reduce<ToolbarOption[]>((arr, command) => {
      switch (command.type) {
        case CommandListEntryType.COMMAND: {
          const captionText = getCaptionText(command.caption, formatMessage);
          const label = getCaptionFullText(captionText, command.hotKey);
          arr.push({
            type: OptionType.BUTTON,
            icon: command.icon,
            name: command.name,
            disabled: !command.available,
            label,
            badge: command.badge ?? badges?.[command.name],
          });
          return arr;
        }
        case CommandListEntryType.SEPARATOR: {
          if (shouldAddSeparator(arr)) {
            arr.push(createSeparator());
          }
          return arr;
        }
        case CommandListEntryType.GROUP: {
          if (shouldAddSeparator(arr)) {
            arr.push(createSeparator());
          }
          arr.push(...createOptions(command.children, formatMessage, badges));
          if (shouldAddSeparator(arr)) {
            arr.push(createSeparator());
          }
          return arr;
        }
        default:
          return arr;
      }
    }, [])
    // delete parameters without implementing onclick logic
    .filter(
      option =>
        !('name' in option) ||
        ![
          BuiltinCaptionType.FILTER_BY_COLUMN,
          BuiltinCaptionType.CLEAR_COLUMNS_FILTERS,
          BuiltinCaptionType.COPY,
          BuiltinCaptionType.EXCEL_EXPORT,
          BuiltinCaptionType.CSV_EXPORT,
        ].includes(option.name as BuiltinCaptionType)
    );
  if (lastElement(options)?.type === OptionType.SEPARATOR) {
    options.pop();
  }
  return options;
};

const getLastShownOptionIndex = (
  options: ToolbarOption[],
  name: string = LAST_SHOWN_BUTTON_NAME
) => {
  const lastButtonIndex = options.findIndex(
    option => option.type !== OptionType.SEPARATOR && option.name === name
  );
  const lastOptionsIndex =
    lastButtonIndex === -1 ? options.length - 1 : lastButtonIndex;
  const nextOptionIndex = lastOptionsIndex + 1;
  const nextOption = options[nextOptionIndex];
  return nextOption?.type === OptionType.SEPARATOR
    ? nextOptionIndex
    : lastOptionsIndex;
};

const CommandsToolbar: React.FC<Props> = ({
  conditionsCount,
  commands,
  onInitiateCommand,
  toolbarCommandsConfig,
  className,
}) => {
  const { formatMessage } = useIntl();
  const options = useMemo(
    () =>
      createOptions(commands ?? [], formatMessage, {
        filter: conditionsCount,
      }).filter(
        command =>
          !Object.entries(toolbarCommandsConfig ?? {}).some(
            ([commandConfigName, { hide, hideInToolbar }]) =>
              'name' in command &&
              command.name === commandConfigName &&
              (hideInToolbar ?? hide)
          )
      ),
    [commands, conditionsCount, formatMessage, toolbarCommandsConfig]
  );
  const lastShownIndex = useMemo(
    () => getLastShownOptionIndex(options),
    [options]
  );

  return (
    <Toolbar
      className={classNames('dataset-toolbar', className)}
      onSelect={onInitiateCommand}
      options={options}
      lastShownIndex={lastShownIndex}
    />
  );
};

export default CommandsToolbar;
